import React, { useState, useRef, useCallback } from 'react';
import { Umpire } from '@/integrations/supabase/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import type { TablesInsert } from '@/integrations/supabase/types';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { X, Pencil } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate, useLocation } from 'react-router-dom';

const initialUmpire: Partial<Umpire> = {
  firstName: '',
  middleName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  nationality: '',
  debutYear: undefined,
  panelType: '',
  primaryRole: '',
  status: '',
  matchesTest: 0,
  matchesOdi: 0,
  matchesT20i: 0,
  matchesFirstClass: 0,
  matchesListA: 0,
  matchesT20Domestic: 0,
  drsDecisionsMade: 0,
  drsDecisionsUpheld: 0,
  drsAccuracyPercent: undefined,
  profilePhoto: '',
  languagesSpoken: '',
  certifications: '',
  remarks: '',
  availabilityStatus: '',
};

const AddUmpirePage: React.FC = () => {
  const [umpire, setUmpire] = useState<Partial<Umpire>>(initialUmpire);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState<string | null>(null);
  const profilePicFileInputRef = useRef<HTMLInputElement>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreviewUrl, setCoverImagePreviewUrl] = useState<string | null>(null);
  const coverImageFileInputRef = useRef<HTMLInputElement>(null);
  const [croppingFor, setCroppingFor] = useState<'profile' | 'cover' | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUmpire((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUmpire((prev) => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    // Basic validation
    if (!umpire.firstName || !umpire.lastName) {
      setError('First name and last name are required.');
      setIsSubmitting(false);
      return;
    }
    // Prepare data for Supabase
    const insertData: TablesInsert<'umpires'> = {
      first_name: umpire.firstName!,
      middle_name: umpire.middleName || '',
      last_name: umpire.lastName!,
      date_of_birth: umpire.dateOfBirth || null,
      gender: umpire.gender || null,
      nationality: umpire.nationality || null,
      debut_year: umpire.debutYear ? Number(umpire.debutYear) : null,
      panel_type: umpire.panelType || null,
      primary_role: umpire.primaryRole || null,
      status: umpire.status || null,
      matches_test: umpire.matchesTest ?? 0,
      matches_odi: umpire.matchesOdi ?? 0,
      matches_t20i: umpire.matchesT20i ?? 0,
      matches_first_class: umpire.matchesFirstClass ?? 0,
      matches_list_a: umpire.matchesListA ?? 0,
      matches_t20_domestic: umpire.matchesT20Domestic ?? 0,
      drs_decisions_made: umpire.drsDecisionsMade ?? 0,
      drs_decisions_upheld: umpire.drsDecisionsUpheld ?? 0,
      drs_accuracy_percent: umpire.drsAccuracyPercent ? Number(umpire.drsAccuracyPercent) : null,
      profile_photo: umpire.profilePhoto || null,
      languages_spoken: umpire.languagesSpoken || null,
      certifications: umpire.certifications || null,
      remarks: umpire.remarks || null,
      availability_status: umpire.availabilityStatus || null,
      // user_id will be set by RLS/auth trigger
    };
    const { error: supabaseError } = await supabase.from('umpires').insert([insertData]);
    if (supabaseError) {
      setError(supabaseError.message);
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    setUmpire(initialUmpire);
    toast({
      title: 'Success',
      description: `Umpire ${umpire.firstName || ''} ${umpire.lastName || ''} added successfully!`,
    });
  };

  const getCroppedImage = useCallback(async (imageSrc: string, crop: Area, rotation = 0): Promise<string> => {
    const image = new window.Image();
    image.src = imageSrc;
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
    });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context');
    const rotationInRadians = (rotation * Math.PI) / 180;
    const originalWidth = image.naturalWidth;
    const originalHeight = image.naturalHeight;
    const sines = Math.abs(Math.sin(rotationInRadians));
    const cosines = Math.abs(Math.cos(rotationInRadians));
    const rotatedWidth = originalWidth * cosines + originalHeight * sines;
    const rotatedHeight = originalWidth * sines + originalHeight * cosines;
    canvas.width = rotatedWidth;
    canvas.height = rotatedHeight;
    ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
    ctx.rotate(rotationInRadians);
    ctx.drawImage(image, -originalWidth / 2, -originalHeight / 2, originalWidth, originalHeight);
    const data = ctx.getImageData(
      rotatedWidth / 2 - crop.width / 2,
      rotatedHeight / 2 - crop.height / 2,
      crop.width,
      crop.height
    );
    canvas.width = crop.width;
    canvas.height = crop.height;
    ctx.clearRect(0, 0, crop.width, crop.height);
    ctx.putImageData(data, 0, 0);
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg');
    });
  }, []);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (type === 'profile') {
        setProfilePictureFile(file);
        setProfilePicturePreviewUrl(URL.createObjectURL(file));
      } else {
        setCoverImageFile(file);
        setCoverImagePreviewUrl(URL.createObjectURL(file));
      }
      setCroppingFor(type);
      setIsCropModalOpen(true);
    }
  };

  const handleCropConfirm = useCallback(async () => {
    let currentPreviewUrl: string | null = null;
    let setFile: React.Dispatch<React.SetStateAction<File | null>>;
    let setPreviewUrl: React.Dispatch<React.SetStateAction<string | null>>;
    let currentFile: File | null = null;
    if (croppingFor === 'profile') {
      currentPreviewUrl = profilePicturePreviewUrl;
      currentFile = profilePictureFile;
      setFile = setProfilePictureFile;
      setPreviewUrl = setProfilePicturePreviewUrl;
    } else {
      currentPreviewUrl = coverImagePreviewUrl;
      currentFile = coverImageFile;
      setFile = setCoverImageFile;
      setPreviewUrl = setCoverImagePreviewUrl;
    }
    if (currentPreviewUrl && croppedAreaPixels && currentFile) {
      const croppedImageUrl = await getCroppedImage(currentPreviewUrl, croppedAreaPixels, rotation);
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const croppedFileName = `${croppingFor}_${currentFile.name}` || `${croppingFor}_image.jpeg`;
      const croppedFile = new File([blob], croppedFileName, {
        type: currentFile?.type || 'image/jpeg',
      });
      setFile(croppedFile);
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
      setPreviewUrl(croppedImageUrl);
      setIsCropModalOpen(false);
      setCroppingFor(null);
    }
  }, [profilePicturePreviewUrl, croppedAreaPixels, rotation, getCroppedImage, profilePictureFile, croppingFor, coverImagePreviewUrl, coverImageFile]);

  const handleCropCancel = () => {
    setIsCropModalOpen(false);
  };

  const handleRemoveProfilePic = () => {
    setProfilePictureFile(null);
    if (profilePicturePreviewUrl) URL.revokeObjectURL(profilePicturePreviewUrl);
    setProfilePicturePreviewUrl(null);
  };

  const handleRemoveCoverImage = () => {
    setCoverImageFile(null);
    if (coverImagePreviewUrl) URL.revokeObjectURL(coverImagePreviewUrl);
    setCoverImagePreviewUrl(null);
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <Button
        variant="ghost"
        onClick={() => {
          const params = new URLSearchParams(location.search);
          const from = params.get('from');
          if (from) {
            navigate(from, { state: location.state });
          } else {
            navigate('/umpires');
          }
        }}
        className="mb-4"
      >
        ← Back {new URLSearchParams(location.search).get('from') ? 'to Schedule Match' : 'to Umpires'}
      </Button>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Add New Umpire</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name fields row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                <Input id="lastName" name="lastName" value={umpire.lastName} onChange={handleChange} required />
              </div>
              <div>
                <Label htmlFor="middleName">Middle Name</Label>
                <Input id="middleName" name="middleName" value={umpire.middleName} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                <Input id="firstName" name="firstName" value={umpire.firstName} onChange={handleChange} required />
              </div>
            </div>
            {/* Profile Picture Upload */}
            <div className="flex flex-col md:flex-row gap-4 items-start w-full">
              <div className="flex flex-col items-center justify-center flex-1">
                <input
                  type="file"
                  ref={profilePicFileInputRef}
                  onChange={e => handleFileChange(e, 'profile')}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Label className="mb-2">Profile Picture</Label>
                {!profilePicturePreviewUrl ? (
                  <div
                    className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                    onClick={() => profilePicFileInputRef.current?.click()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 text-gray-500">
                      <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden relative group cursor-pointer"
                    onClick={() => {
                      setCroppingFor('profile');
                      setIsCropModalOpen(true);
                    }}
                  >
                    <img
                      key={profilePicturePreviewUrl}
                      src={profilePicturePreviewUrl}
                      alt="Profile Picture Preview"
                      className="w-full h-full object-cover rounded-full"
                      onError={e => {
                        e.currentTarget.src = 'https://via.placeholder.com/96';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
                {profilePicturePreviewUrl && (
                  <button type="button" className="mt-2 text-xs text-red-500 underline" onClick={handleRemoveProfilePic}>Remove</button>
                )}
              </div>
              {/* Cover Image Upload */}
              <div className="flex flex-col items-center justify-center flex-1 w-full">
                <input
                  type="file"
                  ref={coverImageFileInputRef}
                  onChange={e => handleFileChange(e, 'cover')}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <Label className="mb-2">Cover Image</Label>
                {!coverImagePreviewUrl ? (
                  <div
                    className="w-full h-24 bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                    onClick={() => coverImageFileInputRef.current?.click()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 text-gray-500">
                      <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-full h-24 overflow-hidden relative group cursor-pointer"
                    onClick={() => {
                      setCroppingFor('cover');
                      setIsCropModalOpen(true);
                    }}
                  >
                    <img
                      key={coverImagePreviewUrl}
                      src={coverImagePreviewUrl}
                      alt="Cover Image Preview"
                      className="w-full h-full object-cover"
                      onError={e => {
                        e.currentTarget.src = 'https://via.placeholder.com/256x96';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
                {coverImagePreviewUrl && (
                  <button type="button" className="mt-2 text-xs text-red-500 underline" onClick={handleRemoveCoverImage}>Remove</button>
                )}
              </div>
            </div>
            {/* Gender, Status, Availability (compulsory) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status <span className="text-red-500">*</span></Label>
                <Select name="status" value={umpire.status || ''} onValueChange={val => setUmpire(prev => ({ ...prev, status: val }))} required>
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Retired">Retired</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                    <SelectItem value="Deceased">Deceased</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="availabilityStatus">Availability <span className="text-red-500">*</span></Label>
                <Select name="availabilityStatus" value={umpire.availabilityStatus || ''} onValueChange={val => setUmpire(prev => ({ ...prev, availabilityStatus: val }))} required>
                  <SelectTrigger id="availabilityStatus">
                    <SelectValue placeholder="Select availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Unavailable">Unavailable</SelectItem>
                    <SelectItem value="Injured">Injured</SelectItem>
                    <SelectItem value="On Leave">On Leave</SelectItem>
                    <SelectItem value="Retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Panel & Role - always visible, not in accordion */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2">Panel & Role</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="panelType">Panel Type</Label>
                  <Select name="panelType" value={umpire.panelType || ''} onValueChange={val => setUmpire(prev => ({ ...prev, panelType: val }))}>
                    <SelectTrigger id="panelType">
                      <SelectValue placeholder="Select panel type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Elite Panel">Elite Panel</SelectItem>
                      <SelectItem value="International Panel">International Panel</SelectItem>
                      <SelectItem value="Development Panel">Development Panel</SelectItem>
                      <SelectItem value="National Panel">National Panel</SelectItem>
                      <SelectItem value="Domestic Panel">Domestic Panel</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="primaryRole">Primary Role</Label>
                  <Select name="primaryRole" value={umpire.primaryRole || ''} onValueChange={val => setUmpire(prev => ({ ...prev, primaryRole: val }))}>
                    <SelectTrigger id="primaryRole">
                      <SelectValue placeholder="Select primary role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On-field Umpire">On-field Umpire</SelectItem>
                      <SelectItem value="Third Umpire (TV)">Third Umpire (TV)</SelectItem>
                      <SelectItem value="Fourth Umpire">Fourth Umpire</SelectItem>
                      <SelectItem value="Match Referee">Match Referee</SelectItem>
                      <SelectItem value="Reserve Umpire">Reserve Umpire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            {/* Accordion for optional sections (Personal Details now includes Gender) */}
            <Accordion type="multiple" className="mt-6">
              <AccordionItem value="personal-details">
                <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                  Personal Details
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <Input id="dateOfBirth" name="dateOfBirth" type="date" value={umpire.dateOfBirth} onChange={handleChange} />
                    </div>
                    <div>
                      <Label htmlFor="nationality">Nationality</Label>
                      <Input id="nationality" name="nationality" value={umpire.nationality} onChange={handleChange} />
                    </div>
                    <div>
                      <Label htmlFor="debutYear">Debut Year</Label>
                      <Input id="debutYear" name="debutYear" type="number" value={umpire.debutYear ?? ''} onChange={handleNumberChange} />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender <span className="text-red-500">*</span></Label>
                      <Select name="gender" value={umpire.gender || ''} onValueChange={val => setUmpire(prev => ({ ...prev, gender: val }))} required>
                        <SelectTrigger id="gender">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                          <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <div className="border-t my-0" />
              <AccordionItem value="experience">
                <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                  Experience
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="matchesTest">Matches Test</Label>
                      <Input id="matchesTest" name="matchesTest" type="number" value={umpire.matchesTest ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="matchesOdi">Matches ODI</Label>
                      <Input id="matchesOdi" name="matchesOdi" type="number" value={umpire.matchesOdi ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="matchesT20i">Matches T20I</Label>
                      <Input id="matchesT20i" name="matchesT20i" type="number" value={umpire.matchesT20i ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="matchesFirstClass">Matches First Class</Label>
                      <Input id="matchesFirstClass" name="matchesFirstClass" type="number" value={umpire.matchesFirstClass ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="matchesListA">Matches List A</Label>
                      <Input id="matchesListA" name="matchesListA" type="number" value={umpire.matchesListA ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="matchesT20Domestic">Matches T20 Domestic</Label>
                      <Input id="matchesT20Domestic" name="matchesT20Domestic" type="number" value={umpire.matchesT20Domestic ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <div className="border-t my-0" />
              <AccordionItem value="drs">
                <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                  DRS
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="drsDecisionsMade">DRS Decisions Made</Label>
                      <Input id="drsDecisionsMade" name="drsDecisionsMade" type="number" value={umpire.drsDecisionsMade ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="drsDecisionsUpheld">DRS Decisions Upheld</Label>
                      <Input id="drsDecisionsUpheld" name="drsDecisionsUpheld" type="number" value={umpire.drsDecisionsUpheld ?? 0} onChange={handleNumberChange} min={0} />
                    </div>
                    <div>
                      <Label htmlFor="drsAccuracyPercent">DRS Accuracy (%)</Label>
                      <Input id="drsAccuracyPercent" name="drsAccuracyPercent" type="number" step="0.01" value={umpire.drsAccuracyPercent ?? ''} onChange={handleNumberChange} min={0} max={100} />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <div className="border-t my-0" />
              <AccordionItem value="other-details">
                <AccordionTrigger className="text-left font-semibold px-0 py-4 hover:bg-transparent focus:bg-transparent focus:outline-none">
                  Other Details
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="languagesSpoken">Languages Spoken</Label>
                      <Input id="languagesSpoken" name="languagesSpoken" value={umpire.languagesSpoken} onChange={handleChange} />
                    </div>
                    <div>
                      <Label htmlFor="certifications">Certifications</Label>
                      <Input id="certifications" name="certifications" value={umpire.certifications} onChange={handleChange} />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="remarks">Remarks</Label>
                      <Input id="remarks" name="remarks" value={umpire.remarks} onChange={handleChange} />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Add Umpire'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddUmpirePage; 